import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import {
  FileWarning, CreditCard, Scale, ShieldAlert, Briefcase, ClipboardCheck,
  Calculator, ArrowRight, Phone, CheckCircle, Clock, MessageSquare, User
} from 'lucide-react';

const services = [
  { icon: FileWarning, title: 'IRS Notices', description: 'We review your notice, explain what it means, and help you respond before deadlines pass.' },
  { icon: CreditCard, title: 'Payment Plans', description: 'We negotiate installment agreements with the IRS that fit your financial situation.' },
  { icon: Scale, title: 'Offers in Compromise', description: 'We evaluate whether you qualify to settle your tax debt for less than the full amount owed.' },
  { icon: ShieldAlert, title: 'Levies & Liens', description: 'We help stop or prevent IRS levies on your bank accounts and liens on your property.' },
  { icon: Briefcase, title: 'Wage Garnishments', description: 'We work to release or reduce IRS wage garnishments so you can keep more of your paycheck.' },
  { icon: ClipboardCheck, title: 'IRS Audits', description: 'We represent you during an IRS audit and make sure your rights are protected.' },
  { icon: Calculator, title: 'Tax Preparation', description: 'Current and past-year returns prepared accurately by experienced tax professionals.' },
];

const steps = [
  { number: '1', title: 'Book a Time That Works for You', description: 'Choose a convenient 30-minute slot using our online scheduling tool. No forms to fill out first.' },
  { number: '2', title: 'Tell Us About Your Situation', description: 'Share the details of your tax situation or IRS notice. Everything you tell us is confidential.' },
  { number: '3', title: 'Get Clear Options and Next Steps', description: 'We explain your options in plain English and recommend the next right step. No pressure, no unrealistic promises.' },
];

const testimonials = [
  {
    quote: 'I received an IRS notice and had no idea what to do. TaxPro - Answers walked me through the entire process and resolved it quickly.',
    name: 'Michael R.',
    role: 'Small Business Owner',
  },
  {
    quote: 'After years of unfiled returns, I was terrified. They made it simple, explained my options, and helped me get back on track.',
    name: 'Sarah K.',
    role: 'Individual Taxpayer',
  },
  {
    quote: 'Clear, honest, and professional. No scare tactics, no upselling. Just solid advice and real help with my tax problem.',
    name: 'David L.',
    role: 'Self-Employed Professional',
  },
];

export default function FreeConsultation() {
  useSEO({
    title: 'Free 30-Minute Tax Consultation | TaxPro-Answers Charlotte NC',
    description: 'Free 30-Minute Tax Consultation | TaxPro-Answers Charlotte NC. Tax resolution and tax preparation with 25+ years of experience. Get clear answers about your IRS problem with no pressure.',
  });

  return (
    <div data-testid="free-consultation-page">
      {/* Hero */}
      <section className="bg-[#1F2E5A] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#2E7D32]/15 rounded-full text-xs font-semibold uppercase tracking-widest text-[#2E7D32] mb-6">
              <Clock className="w-3.5 h-3.5" /> Free 30-Minute Consultation
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-white mb-6">
              Free 30-Minute Tax Consultation
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10">
              Tax Resolution &amp; Tax Preparation with 25+ years of experience. Get clear, plain-English answers about your IRS problem and your options, with no pressure and no unrealistic promises.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="hero-book-btn"
                  className="bg-[#2E7D32] hover:bg-[#256b29] text-white font-semibold px-8 py-7 text-base rounded-md w-full sm:w-auto"
                >
                  Book Your Free Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="tel:+18555892461">
                <Button
                  data-testid="hero-call-btn"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-7 text-base rounded-md w-full sm:w-auto"
                >
                  <Phone className="w-4 h-4 mr-2" /> Call (855) 589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2E7D32] mb-4">
              How We Can Help
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#1F2E5A] mb-14 max-w-lg">
              Tax resolution and preparation services for individuals and businesses
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {services.map((service, i) => (
              <AnimatedSection key={i} delay={i * 0.06}>
                <div className="bg-white p-8 h-full">
                  <div className="w-11 h-11 rounded-md bg-[#1F2E5A]/5 flex items-center justify-center mb-5">
                    <service.icon className="w-5 h-5 text-[#1F2E5A]" />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-[#1F2E5A] mb-2">{service.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{service.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2E7D32] mb-4">
              What to Expect
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#1F2E5A] mb-14 max-w-lg">
              What to expect in your free 30 minutes
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className="relative">
                  <span className="font-heading font-bold text-6xl text-[#1F2E5A]/5 absolute -top-4 -left-2">
                    0{step.number}
                  </span>
                  <div className="relative pt-8">
                    <div className="w-12 h-12 rounded-lg bg-[#1F2E5A] flex items-center justify-center mb-5">
                      <span className="text-[#2E7D32] font-heading font-bold text-lg">{step.number}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-[#1F2E5A] mb-3">
                      {step.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* About Bob Mickerson */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2E7D32] mb-4">
                Your Tax Professional
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#1F2E5A] mb-6">
                Bob Mickerson, Tax Resolution Specialist
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                With more than 25 years of experience, Bob Mickerson has helped individuals and business owners across North Carolina resolve IRS problems, file past-due returns, negotiate payment plans, and get back on solid ground. His approach is simple: confidential, professional, plain-English guidance with no scare tactics and no unrealistic promises.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  '25+ years of tax experience',
                  'IRS notice and resolution expert',
                  'Individual and business clients',
                  'Plain-English, no-hype approach',
                  'Confidential and professional',
                  'Charlotte, NC based',
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-[#2E7D32] flex-shrink-0" />
                    <span className="text-sm text-slate-700">{point}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <div className="bg-[#1F2E5A] rounded-lg p-8 text-white">
                <User className="w-10 h-10 text-[#2E7D32] mb-5" />
                <h3 className="font-heading font-bold text-xl mb-4">Why Clients Choose Us</h3>
                <div className="space-y-4">
                  <p className="text-sm text-slate-300 leading-relaxed">
                    "We don't make promises we can't keep. Every situation is different, and we'll tell you the truth about your options — even if it's not what you want to hear. That honesty is why clients trust us."
                  </p>
                  <p className="text-sm text-slate-400">— Bob Mickerson</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2E7D32] mb-4">
              Client Experiences
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#1F2E5A] mb-14 max-w-lg">
              Real results from real clients
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="bg-white border border-slate-200 rounded-lg p-8 h-full flex flex-col">
                  <div className="flex gap-1 mb-5">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-4 h-4 bg-[#2E7D32] rounded-full" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6 flex-1">
                    "{t.quote}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-[#1F2E5A]">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.role}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-[#1F2E5A]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-white mb-4">
              Ready to get clear answers?
            </h2>
            <p className="text-base text-slate-400 mb-10 max-w-lg mx-auto">
              Book your free 30-minute consultation. No commitment, no pressure — just honest, experienced tax guidance.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="final-book-btn"
                  className="bg-[#2E7D32] hover:bg-[#256b29] text-white font-semibold px-8 py-7 text-base rounded-md w-full sm:w-auto"
                >
                  Book Your Free Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="tel:+18555892461">
                <Button
                  data-testid="final-call-btn"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-7 text-base rounded-md w-full sm:w-auto"
                >
                  <Phone className="w-4 h-4 mr-2" /> Call (855) 589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer Contact */}
      <section className="py-12 bg-[#1F2E5A] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-400 mb-1 font-medium">TaxPro-Answers | RCC Consultants Group LLC</p>
          <p className="text-xs text-slate-500 mb-1">10926 David Taylor Dr Ste 120-2108, Charlotte, NC 28262</p>
          <p className="text-xs text-slate-500 mb-1">Meetings by appointment</p>
          <p className="text-xs text-slate-500">
            <a href="tel:+18555892461" className="hover:text-white transition-colors">(855) 589-2461</a>
            {' | '}
            <a href="mailto:info@taxpro-answers.com" className="hover:text-white transition-colors">info@taxpro-answers.com</a>
          </p>
        </div>
      </section>
    </div>
  );
}
